// TextsOnCallsSection.tsx · v12.10.27 (#2150 Practice name on texts) · 2026-10-31 · VoiceSpectrum AI
// TextsOnCallsSection.tsx · v12.10.26 (#T154 Settings › Call Handling › Texts on calls - UI-PROTO-T154 v1.0 screen 1; Q1 Q2 Q3 Q4 Q11 Q19 Q20 TC9) · 2026-10-30 · VoiceSpectrum AI
// Text replies On / Off (new practice Off), reply window (7 days), the automatic text when Off (EN / ES / ZH + {organization.phone}),
// the outgoing wording (read-only - follows Text replies), Direct calls to the VoiceSpectrum number Allow / Don't allow (new practice Allow).
// Its own section card with Discard / Save section; its dirty state is reported to CallHandlingSections (the sidebar red dot).
import { ReactNode, useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api, { ApiError } from '../lib/api';
import { saveErrorToast } from '../lib/saveError';
import { toast } from '../lib/toast';
import { HelpTooltip } from '../components/aiagent/HelpTooltip';
import TextLangEditor from '../components/texting/TextLangEditor';
import { closingLine, renderPhone } from '../lib/texting';
import '../components/texting/texting.css';

interface TextsOnCalls {
  repliesOn: boolean; replyWindowDays: number; directCalls: string; autoText: Record<string, string>;
  defaultAutoText: Record<string, string>; officePhone?: string | null;
  senderName?: string | null; displayName?: string | null;   // v12.10.27 (#2150)
}
type Draft = Pick<TextsOnCalls, 'repliesOn' | 'replyWindowDays' | 'directCalls' | 'autoText'> & { senderName: string };
export interface TextsOnCallsState { dirty: boolean; save: () => Promise<void>; discard: () => void }

const QKEY = ['t2-texts-on-calls'];
const toDraft = (d: TextsOnCalls): Draft => ({ repliesOn: d.repliesOn, replyWindowDays: d.replyWindowDays, directCalls: d.directCalls, autoText: { ...d.autoText }, senderName: d.senderName ?? '' });

export default function TextsOnCallsSection({ onState }: { onState?: (s: TextsOnCallsState | null) => void }) {
  const qc = useQueryClient();
  const { data, error, isLoading } = useQuery<TextsOnCalls>({ queryKey: QKEY, queryFn: () => api.get<TextsOnCalls>('/t2/settings/texts-on-calls'), retry: false });
  const [saved, setSaved] = useState<Draft | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  useEffect(() => { if (data) { setSaved(toDraft(data)); setDraft(toDraft(data)); } }, [data]);

  const save = useMutation({
    mutationFn: (d: Draft) => api.put<{ saved: boolean }>('/t2/settings/texts-on-calls', d),
    onSuccess: () => {
      setSaved(draft ? structuredClone(draft) : saved);
      qc.invalidateQueries({ queryKey: QKEY });
      qc.invalidateQueries({ queryKey: ['t2-texting-status'] });
      toast('success', 'Texts on calls saved');
    },
    onError: (err) => saveErrorToast(err, 'Texts on calls'),
  });

  const dirty = !!draft && !!saved && JSON.stringify(draft) !== JSON.stringify(saved);
  const report = useRef(onState); report.current = onState;
  useEffect(() => {
    report.current?.(draft ? {
      dirty,
      save: async () => { if (dirty && draft) { try { await save.mutateAsync(draft); } catch { /* toast */ } } },
      discard: () => setDraft(saved ? structuredClone(saved) : draft),
    } : null);
  }, [dirty, draft, saved]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => report.current?.(null), []);

  // No View right on this section: the row is not shown (the sidebar hides it too).
  if (error instanceof ApiError && error.status === 403) return null;
  if (isLoading || !draft || !saved || !data) return <div className="settings-loading">Loading Texts on calls…</div>;
  if (error) return <div className="settings-error">Texts on calls could not be loaded.</div>;

  const patch = (p: Partial<Draft>) => setDraft((d) => (d ? { ...d, ...p } : d));
  const autoDirty = JSON.stringify(draft.autoText) !== JSON.stringify(saved.autoText);
  const block = draft.directCalls === 'block';

  return (
    <Section title="Texts on calls" badge="NEW" dirty={dirty} saving={save.isPending}
      description="Texts we send after a call (or staff send by hand), what happens when a patient replies, and calls that come in on the VoiceSpectrum number."
      onSave={() => save.mutate(draft)} onDiscard={() => setDraft(structuredClone(saved))}>
      <div className="tx-row">
        <div className="tx-row-main">
          <b>Text replies</b><span className="tx-default">New practice default: Off</span>
          <HelpTooltip sections={[['On', 'A patient’s reply to a text we sent is added to that call’s record, marked New text, and the follow-up flag turns on.'], ['Off', 'The patient gets the automatic text below - nothing else is shown or saved.']]} />
          <span className="tx-row-sub">On: a patient’s reply to a text we sent is added to that call’s record, marked <b>New text</b>, and the follow-up flag turns on. Off: the patient gets the automatic text below - nothing else is shown or saved.</span>
        </div>
        <div className="seg-group" role="radiogroup" aria-label="Text replies">
          <button type="button" className={`seg${!draft.repliesOn ? ' on' : ''}`} onClick={() => patch({ repliesOn: false })}><span className="seg-dot" />Off</button>
          <button type="button" className={`seg${draft.repliesOn ? ' on' : ''}`} onClick={() => patch({ repliesOn: true })}><span className="seg-dot" />On</button>
        </div>
      </div>

      {/* v12.10.27 (#2150, Tushar 10/31 "this has to be standard: we can always add prefix 'Dr. Serena Lee's Office' or 'Union Medical
          Group'"): the name every text starts with - blank = Organization › Display name. */}
      <div className="tx-row">
        <div className="tx-row-main">
          <b>Practice name on texts</b>
          <span className="tx-row-sub">Every text we send starts with it - “{(draft.senderName.trim() || data.displayName || 'Your practice')}: Your reference number for today’s call is 44-2.” Blank = Organization › Display name{data.displayName ? ` (${data.displayName})` : ''}.</span>
        </div>
        <div className="tx-num">
          <input type="text" maxLength={60} value={draft.senderName} placeholder={data.displayName ?? ''} style={{ width: 240 }}
            onChange={(e) => patch({ senderName: e.target.value })} aria-label="Practice name on texts" />
        </div>
      </div>

      <div className="tx-row">
        <div className="tx-row-main">
          <b>Reply window</b>
          <span className="tx-row-sub">A reply joins the call behind the most recent text we sent to that phone from this practice’s number, if it comes within this many days. Later replies start a new conversation under Text Messages (or get the Text Messaging automatic reply when that workflow is off). Each text keeps its call, so two patients on one phone land on the right call.</span>
        </div>
        <div className="tx-num">
          <input type="number" min={1} max={90} value={draft.replyWindowDays}
            onChange={(e) => patch({ replyWindowDays: Math.max(1, Math.min(90, Number(e.target.value) || 7)) })} aria-label="Reply window in days" />
          <span>days</span><span className="tx-default">Default: 7</span>
        </div>
      </div>

      <div className={`tx-row${autoDirty ? ' tx-amber' : ''}`}>
        <div className="tx-row-main">
          <b>Automatic text when Text replies is Off</b>
          {draft.repliesOn ? <span className="tx-default">not in use - Text replies is On</span> : <span className="tx-inuse">IN USE</span>}
          <TextLangEditor value={draft.autoText} defaults={data.defaultAutoText} officePhone={data.officePhone} onChange={(autoText) => patch({ autoText })} />
          <span className="tx-row-sub">Sent once, automatically, when a patient replies to one of our texts and Text replies is Off. Nothing is saved on the call and no flag is set. Language: the call’s language → the contact’s Preferred language → the practice’s default.</span>
        </div>
      </div>

      <div className="tx-row">
        <div className="tx-row-main">
          <b>Outgoing wording</b>
          <span className="tx-row-sub">Follows Text replies - our texts point patients to the main office line, never the VoiceSpectrum number.</span>
          <div className="tx-wording">
            <div className={draft.repliesOn ? 'tx-dim' : ''}>Text replies Off: “… {renderPhone(closingLine('en', false), data.officePhone)}”{!draft.repliesOn && <span className="tx-inuse">IN USE</span>}</div>
            <div className={draft.repliesOn ? '' : 'tx-dim'}>Text replies On: “… {renderPhone(closingLine('en', true), data.officePhone)}”{draft.repliesOn && <span className="tx-inuse">IN USE</span>}</div>
          </div>
        </div>
      </div>

      <div className="tx-row">
        <div className="tx-row-main">
          <b>Direct calls to the VoiceSpectrum number</b><span className="tx-default">New practice default: Allow</span>
          <span className="tx-row-sub">{block
            ? 'Don’t allow: a call Twilio shows as dialled straight to the VoiceSpectrum number is forwarded to the main office line, without a word, and logged as “Forwarded to office (direct call)”. A call forwarded by your phone system - or one we can’t tell - is answered as usual.'
            : 'Allow: every call to the VoiceSpectrum number is answered as today.'}</span>
        </div>
        <div className="seg-group" role="radiogroup" aria-label="Direct calls">
          <button type="button" className={`seg${!block ? ' on' : ''}`} onClick={() => patch({ directCalls: 'allow' })}><span className="seg-dot" />Allow</button>
          <button type="button" className={`seg${block ? ' on' : ''}`} onClick={() => patch({ directCalls: 'block' })}><span className="seg-dot" />Don’t allow</button>
        </div>
      </div>

      <p className="tx-rights">Rights: Call Handling › Texts on calls - View + Edit (admin groups by default). Every change here and every automatic text sent is written to the audit log.</p>
    </Section>
  );
}

function Section(props: { title: string; badge?: string; description: string; dirty: boolean; saving: boolean; onSave: () => void; onDiscard: () => void; children: ReactNode }) {
  return (
    <section className={`settings-section-card${props.dirty ? ' dirty' : ''}`}>
      <header className="settings-section-header">
        <div className="settings-section-headrow">
          <h2>{props.title}{props.badge && <span className="tx-default">{props.badge}</span>}</h2>
          <div className="settings-actions">
            <button type="button" className="settings-secondary" disabled={!props.dirty || props.saving} onClick={props.onDiscard}>Discard</button>
            <button type="button" className="settings-primary" disabled={!props.dirty || props.saving} onClick={props.onSave}>{props.saving ? 'Saving…' : 'Save section'}</button>
          </div>
        </div>
        <p className="ch-sec-desc">{props.description}</p>
      </header>
      <div className="ch-section-body">{props.children}</div>
    </section>
  );
}
